import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { configureStore } from '@reduxjs/toolkit';
import { syncHistoryWithStore } from 'react-router-redux';
import createSagaMiddleware from 'redux-saga';
import mainSaga from './sagas';
import Root from './config/Root';
import 'bootstrap/dist/css/bootstrap.css';
import './styles/main.nohash.less';
import './styles/bootstrap-override.less';
import { register } from 'swiper/element/bundle';
import rootReducer from './reducers';
import { createBrowserHistory } from 'history';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { preloadedState } from './preloaded-state';
import './fonts/allfonts.css';
import { init } from './ducks/init';
import { showGenericErrorModal } from './ducks/modals';
import { setError } from './ducks/error';
import { logEvent } from './utils/appLog';
import ReactGA from 'react-ga4';
import { isGammaOrProd, isProd } from './config';
import Cookies from 'universal-cookie';
import { initializeRum } from './utils/rum';
import { initializeCtEvents } from './utils/ct-events';
import { initializeNewRelic } from './utils/new-relic';
import { configureAppSync } from './utils/appSyncService';

console.log(
  `%cBookWright Online %c// v${__VERSION__}`,
  'background-color: #dd8132; padding: 1rem; padding-right: 0; font-weight: bold',
  'background-color: #dd8132; padding: 1rem; padding-left: 0; font-weight: normal',
);

const userType = new Cookies().get('user-type');
if (userType) {
  console.log(`Found user-type cookie: ${userType}`);
}

const enableMetrics = userType !== 'canary' && userType !== 'renderer';

if (enableMetrics && isGammaOrProd()) {
  initializeNewRelic();
  initializeRum();
  initializeCtEvents();

  if (isProd()) {
    // We don't have a non-prod endpoint for GA
    ReactGA.initialize('G-LLWHQP8KM8');
  } else {
    console.log('GA is disabled in gamma');
  }
} else {
  console.log('Client-side metrics are disabled');
}

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(
    resourcesToBackend(
      (language, namespace) => import(`./locales/${language}.json`),
    ),
  )
  .init({
    // debug: true,
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  });

const sagaMiddleware = createSagaMiddleware({
  onError(error, errorInfo) {
    //this will handle saga errors that bubble up
    console.error(error, errorInfo);
    store.dispatch(setError(error.message));
    logEvent(error);
  },
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(sagaMiddleware),
  preloadedState,
});

const browserHistory = createBrowserHistory();
const history = syncHistoryWithStore(browserHistory, store);
history.push(history.location);

// Configure AppSync for real-time updates
configureAppSync();

// Run the main saga to do stuff...
sagaMiddleware.run(mainSaga);

store.dispatch(init());

const container = document.getElementById('app');
const root = createRoot(container);

root.render(<Root store={store} history={history} />);

register();

//this will handle async methods and click handlers
// error boundaries do not work for those errors
window.onerror = (message, _source, _lineno, _colno, error) => {
  store.dispatch(setError(message));
  logEvent(error);
};

window.testErrorModal = () => {
  store.dispatch(showGenericErrorModal());
};
